import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle, createContext, useContext } from 'react';
import maplibregl from 'maplibre-gl';

// Create context for map instance
const MapContext = createContext<maplibregl.Map | null>(null);

// Hook to use map context
export const useMap = () => {
  const map = useContext(MapContext);
  if (!map) {
    throw new Error('useMap must be used within a MapLibreMap component');
  }
  return map;
};

interface MapLibreMapProps {
  initialViewState: {
    latitude: number;
    longitude: number;
    zoom: number;
  };
  style?: React.CSSProperties;
  onMove?: (evt: any) => void;
  children?: React.ReactNode;
}

export interface MapLibreMapRef {
  flyTo: (options: any) => void;
  getCenter: () => { lng: number; lat: number };
  getZoom: () => number;
  getContainer: () => HTMLDivElement | null;
  resize: () => void;
}

const MapLibreMap = forwardRef<MapLibreMapRef, MapLibreMapProps>(({
  initialViewState,
  style,
  onMove,
  children
}, ref) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapId = useRef(`map-${Math.random().toString(36).substr(2, 9)}`);

  // Keep the latest onMove without recreating the map, and throttle move
  // events to at most one per animation frame. MapLibre fires 'move' many
  // times per second during a pan/zoom; calling onMove (which drives parent
  // setState) on every one of those re-renders the whole app continuously
  // and, on constrained browsers, crashes the tab.
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const moveRafRef = useRef<number | null>(null);

  // Expose map methods to parent component
  useImperativeHandle(ref, () => ({
    flyTo: (options: any) => {
      if (map.current) {
        map.current.flyTo(options);
      }
    },
    getCenter: () => {
      if (map.current) {
        return map.current.getCenter();
      }
      return { lng: 0, lat: 0 };
    },
    getZoom: () => {
      if (map.current) {
        return map.current.getZoom();
      }
      return 0;
    },
    getContainer: () => {
      return mapContainer.current;
    },
    resize: () => {
      if (map.current) {
        map.current.resize();
      }
    }
  }));

  useEffect(() => {
    // Initialize map only once
    
    if (map.current) {
      return; // Initialize map only once
    }

    if (mapContainer.current) {
      map.current = new maplibregl.Map({
        container: mapContainer.current,
        style: {
          version: 8,
          sources: {
            'osm': {
              type: 'raster',
              tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
              tileSize: 256,
              attribution: '© OpenStreetMap contributors'
            }
          },
          layers: [
            {
              id: 'osm-tiles',
              type: 'raster',
              source: 'osm',
              minzoom: 0,
              maxzoom: 22
            }
          ]
        },
        center: [initialViewState.longitude, initialViewState.latitude],
        zoom: initialViewState.zoom,
        attributionControl: false,
        // Enable all map interactions
        dragRotate: true,
        dragPan: true,
        scrollZoom: true,
        boxZoom: true,
        doubleClickZoom: true,
        touchZoomRotate: true,
        keyboard: true
      });

      map.current.on('load', () => {
        setMapLoaded(true);
      });

      // Throttled move handler: 'move' fires for both pan and zoom, so we
      // only subscribe once. requestAnimationFrame coalesces the burst of
      // events into a single onMove per frame.
      const scheduleMove = () => {
        if (moveRafRef.current !== null) return;
        moveRafRef.current = requestAnimationFrame(() => {
          moveRafRef.current = null;
          if (onMoveRef.current && map.current) {
            const center = map.current.getCenter();
            onMoveRef.current({
              viewState: {
                longitude: center.lng,
                latitude: center.lat,
                zoom: map.current.getZoom()
              }
            });
          }
        });
      };
      map.current.on('move', scheduleMove);

      // Degrade gracefully if the GPU drops the WebGL context (common on
      // constrained in-car browsers) instead of throwing/white-screening.
      map.current.on('webglcontextlost', () => {
        console.warn('[map] WebGL context lost — will attempt to restore');
      });
      map.current.on('webglcontextrestored', () => {
        console.warn('[map] WebGL context restored');
        if (map.current) {
          map.current.resize();
        }
      });

      // Navigation control disabled - using custom controls that center on user location
      // map.current.addControl(new maplibregl.NavigationControl(), 'top-left');
    }

    return () => {
      if (moveRafRef.current !== null) {
        cancelAnimationFrame(moveRafRef.current);
        moveRafRef.current = null;
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []); // Empty dependency array to ensure map is created only once

  return (
    <MapContext.Provider value={map.current}>
      <div ref={mapContainer} style={style}>
        {mapLoaded && children}
      </div>
    </MapContext.Provider>
  );
});

MapLibreMap.displayName = 'MapLibreMap';

export default MapLibreMap;
